import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TOUR_STEPS } from "../lib/helpContent";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  LayoutDashboard,
  FileText,
  Trello,
  ShieldCheck,
  Briefcase,
  ExternalLink,
} from "lucide-react";

const ICON_MAP = {
  Sparkles,
  LayoutDashboard,
  FileText,
  Trello,
  ShieldCheck,
  Briefcase,
};

export default function InteractiveTour({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const step = TOUR_STEPS[idx];
  const isLast = idx === TOUR_STEPS.length - 1;

  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") setIdx((i) => Math.min(TOUR_STEPS.length - 1, i + 1));
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !step) return null;

  const Icon = ICON_MAP[step.icon] || Sparkles;

  const handleGoto = () => {
    if (step.goto) {
      onClose?.();
      navigate(step.goto);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60"
      data-testid="interactive-tour"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="relative w-full max-w-xl bg-white border-2 border-neutral-900 shadow-[12px_12px_0_0_rgba(0,0,0,0.18)] mx-4"
        data-testid={`tour-step-${idx}`}
      >
        {/* Header strip */}
        <div
          className="h-2 transition-colors duration-500"
          style={{ backgroundColor: step.accent }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close tour"
          data-testid="tour-close-btn"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center hover:bg-neutral-100"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Body */}
        <div className="p-8 lg:p-10">
          <div
            className="w-12 h-12 flex items-center justify-center text-white mb-5 transition-colors duration-500"
            style={{ backgroundColor: step.accent }}
          >
            <Icon className="w-6 h-6" />
          </div>

          <h2 className="font-display font-black text-3xl tracking-tighter leading-[0.95]">
            {step.title}
          </h2>
          <p className="mt-4 text-base text-neutral-700 leading-relaxed">
            {step.body}
          </p>

          {step.goto && (
            <button
              type="button"
              onClick={handleGoto}
              data-testid="tour-goto-btn"
              className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#4A4458] hover:text-black"
            >
              Open this section
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-8 lg:px-10 py-5 border-t border-neutral-200 bg-neutral-50">
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            data-testid="tour-prev-btn"
            className="w-9 h-9 flex items-center justify-center border border-neutral-300 hover:border-neutral-900 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex-1 flex gap-1" data-testid="tour-progress">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1 flex-1"
                style={{
                  backgroundColor:
                    i <= idx ? step.accent : "rgba(0,0,0,0.12)",
                  transition: "background-color 250ms",
                }}
              />
            ))}
          </div>

          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">
            {String(idx + 1).padStart(2, "0")}/
            {String(TOUR_STEPS.length).padStart(2, "0")}
          </span>

          {isLast ? (
            <button
              type="button"
              onClick={onClose}
              data-testid="tour-finish-btn"
              className="h-9 px-4 inline-flex items-center gap-1.5 bg-neutral-900 hover:bg-[#7C5FB0] text-white text-xs uppercase tracking-[0.25em] font-bold"
            >
              Finish
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIdx((i) => Math.min(TOUR_STEPS.length - 1, i + 1))}
              data-testid="tour-next-btn"
              className="h-9 px-4 inline-flex items-center gap-1.5 bg-neutral-900 hover:bg-[#4A4458] text-white text-xs uppercase tracking-[0.25em] font-bold"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
